/**
 * Reliability Export Engine - Phase 14
 * 
 * Export reliability reports in various formats.
 */

import { 
  getMerchantReliabilitySummary,
  getRepeatedIssueEntities,
  getBlockedPaymentReliability,
  getReliabilityStatusBreakdown
} from './reliabilitySelectors';
import { RELIABILITY_STATUS_CONFIG, computeAverageResolutionTime } from './reliabilityModel';

// ==================== EXPORT FUNCTIONS ====================

/**
 * Export reliability summary as CSV
 * @param {Object} summary - Reliability summary object
 * @param {string} filename - Optional filename
 */
export function exportReliabilitySummaryCsv(summary, filename = null) {
  const rows = buildReliabilitySummaryRows(summary);
  const csv = convertRowsToCsv(rows);
  
  const exportFilename = filename || buildReliabilityFilename(summary, 'csv');
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), exportFilename);
  
  recordReliabilityExport('summary_csv', summary);
}

/**
 * Export reliability summary as JSON
 * @param {Object} summary
 * @param {string} filename
 */
export function exportReliabilitySummaryJson(summary, filename = null) {
  const exportFilename = filename || buildReliabilityFilename(summary, 'json');
  const blob = new Blob([JSON.stringify(summary, null, 2)], { type: 'application/json' });
  downloadBlob(blob, exportFilename);
  
  recordReliabilityExport('summary_json', summary);
}

/**
 * Export entity table (locations/terminals) as CSV
 * @param {Array} entities - Array of entity reliability data
 * @param {string} entityType - 'location' | 'terminal'
 */
export function exportEntityTableCsv(entities, entityType = 'location') {
  const rows = entities.map(entity => ({
    ID: entity.id || entity.entityId,
    Name: entity.entityName || entity.id,
    Status: entity.status || entity.reliabilityStatus,
    Score: entity.score || entity.reliabilityScore,
    'Incident Count': entity.incidentCount || entity.count,
    'Critical Count': entity.critical || entity.core?.criticalIncidentCount || 0,
    'Blocked Payments': entity.blocked || entity.core?.blockedPaymentCount || 0
  }));
  
  const csv = convertRowsToCsv(rows);
  const filename = `asterpos-reliability-${entityType}s-${formatDateForFilename()}.csv`;
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename);
  
  recordReliabilityExport('entity_table_csv', { entityType, count: entities.length });
}

/**
 * Export blocked payment follow-up list as CSV
 * @param {Object} filters
 */
export async function exportBlockedPaymentFollowupCsv(filters = {}) {
  const data = await getBlockedPaymentReliability(filters);
  
  const rows = [
    { Metric: 'Total Payment Incidents', Value: data.totalPaymentIncidents },
    { Metric: 'Currently Blocked', Value: data.blockedCount },
    { Metric: 'Resolved', Value: data.resolvedCount },
    { Metric: 'Recovery Rate', Value: `${data.recoveryRate.toFixed(1)}%` },
    { Metric: 'Sync Success Rate', Value: `${data.syncSuccessRate.toFixed(1)}%` },
    { Metric: 'Unresolved Blocked', Value: data.unresolvedBlocked }
  ];
  
  const csv = convertRowsToCsv(rows);
  const filename = `asterpos-blocked-payments-followup-${formatDateForFilename()}.csv`;
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename);
  
  recordReliabilityExport('blocked_payments_csv', filters);
}

/**
 * Export repeated issues report as CSV
 * @param {Object} filters
 */
export async function exportRepeatedIssuesCsv(filters = {}) {
  const data = await getRepeatedIssueEntities(filters);
  
  // Combine locations and terminals
  const rows = [];
  
  // Location section
  rows.push({ Type: '--- LOCATIONS ---', ID: '', Count: '', Critical: '', Blocked: '' });
  for (const loc of data.locations) {
    rows.push({
      Type: 'Location',
      ID: loc.id,
      Count: loc.count,
      Critical: loc.critical,
      Blocked: loc.blocked
    });
  }
  
  // Terminal section
  rows.push({ Type: '--- TERMINALS ---', ID: '', Count: '', Critical: '', Blocked: '' });
  for (const term of data.terminals) {
    rows.push({
      Type: 'Terminal',
      ID: term.id,
      Count: term.count,
      Critical: term.critical,
      Blocked: term.blocked
    });
  }
  
  const csv = convertRowsToCsv(rows);
  const filename = `asterpos-repeated-issues-${formatDateForFilename()}.csv`;
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename);
  
  recordReliabilityExport('repeated_issues_csv', filters);
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Build CSV rows from reliability summary
 * @param {Object} summary
 * @returns {Array}
 */
function buildReliabilitySummaryRows(summary) {
  const rows = [];
  const statusConfig = RELIABILITY_STATUS_CONFIG[summary.reliabilityStatus];
  
  // Header info
  rows.push({
    Category: 'Overview',
    Metric: 'Entity',
    Value: `${summary.entityType}: ${summary.entityId}`,
    Notes: summary.entityName
  });
  
  rows.push({
    Category: 'Overview',
    Metric: 'Status',
    Value: statusConfig?.label || summary.reliabilityStatus,
    Notes: statusConfig?.description || ''
  });
  
  rows.push({
    Category: 'Overview',
    Metric: 'Score',
    Value: summary.reliabilityScore,
    Notes: 'Out of 100'
  });
  
  rows.push({
    Category: 'Overview',
    Metric: 'Generated At',
    Value: new Date(summary.computedAt).toISOString(),
    Notes: ''
  });
  
  // Core metrics
  rows.push({
    Category: 'Core Metrics',
    Metric: 'Total Incidents',
    Value: summary.core.offlineIncidentCount,
    Notes: ''
  });
  
  rows.push({
    Category: 'Core Metrics',
    Metric: 'Unresolved',
    Value: summary.core.unresolvedIncidentCount,
    Notes: summary.core.unresolvedIncidentCount > 0 ? 'ACTION NEEDED' : ''
  });
  
  rows.push({
    Category: 'Core Metrics',
    Metric: 'Critical',
    Value: summary.core.criticalIncidentCount,
    Notes: summary.core.criticalIncidentCount > 0 ? 'HIGH PRIORITY' : ''
  });
  
  rows.push({
    Category: 'Core Metrics',
    Metric: 'Blocked Payments',
    Value: summary.core.blockedPaymentCount,
    Notes: summary.core.blockedPaymentCount > 0 ? 'REVENUE IMPACT' : ''
  });
  
  rows.push({
    Category: 'Core Metrics',
    Metric: 'Sync Success Rate',
    Value: `${summary.core.syncSuccessRate.toFixed(1)}%`,
    Notes: ''
  });
  
  rows.push({
    Category: 'Core Metrics',
    Metric: 'Conflict Rate',
    Value: `${summary.core.conflictRate.toFixed(1)}%`,
    Notes: ''
  });
  
  rows.push({
    Category: 'Core Metrics',
    Metric: 'Avg Resolution Time',
    Value: computeAverageResolutionTime(summary.core.averageResolutionTimeMs),
    Notes: ''
  });
  
  rows.push({
    Category: 'Core Metrics',
    Metric: 'Affected Terminals',
    Value: summary.core.affectedTerminalsCount,
    Notes: ''
  });
  
  rows.push({
    Category: 'Core Metrics',
    Metric: 'Affected Locations',
    Value: summary.core.affectedLocationsCount,
    Notes: ''
  });
  
  // POS metrics
  rows.push({
    Category: 'POS Metrics',
    Metric: 'Unsynced Orders',
    Value: summary.pos.unsyncedOrdersCount,
    Notes: ''
  });
  
  rows.push({
    Category: 'POS Metrics',
    Metric: 'Order Replay Success',
    Value: `${summary.pos.orderReplaySuccessRate.toFixed(1)}%`,
    Notes: ''
  });
  
  rows.push({
    Category: 'POS Metrics',
    Metric: 'Payment Recovery Success',
    Value: `${summary.pos.paymentRecoverySuccessRate.toFixed(1)}%`,
    Notes: ''
  });
  
  rows.push({
    Category: 'POS Metrics',
    Metric: 'Failed Kitchen',
    Value: summary.pos.failedKitchenCount,
    Notes: ''
  });
  
  rows.push({
    Category: 'POS Metrics',
    Metric: 'Failed Receipts',
    Value: summary.pos.failedReceiptsCount,
    Notes: ''
  });
  
  // Staff metrics
  rows.push({
    Category: 'Staff Metrics',
    Metric: 'Offline Staff Actions',
    Value: summary.staff.offlineStaffActionsCount,
    Notes: ''
  });
  
  rows.push({
    Category: 'Staff Metrics',
    Metric: 'Availability Replay Success',
    Value: `${summary.staff.availabilityReplaySuccessRate.toFixed(1)}%`,
    Notes: ''
  });
  
  rows.push({
    Category: 'Staff Metrics',
    Metric: 'Schedule Ack Replay Success',
    Value: `${summary.staff.scheduleAckReplaySuccessRate.toFixed(1)}%`,
    Notes: ''
  });
  
  return rows;
}

/**
 * Build filename for reliability export
 * @param {Object} summary
 * @param {string} format
 * @returns {string}
 */
function buildReliabilityFilename(summary, format) {
  const parts = ['asterpos-reliability'];
  parts.push(summary.entityType);
  parts.push(summary.entityId);
  parts.push(formatDateForFilename());
  return `${parts.join('-')}.${format}`;
}

/**
 * Format date for filename
 * @returns {string}
 */
function formatDateForFilename() {
  return new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
}

/**
 * Convert rows to CSV string
 * @param {Array} rows
 * @returns {string}
 */
function convertRowsToCsv(rows) {
  if (rows.length === 0) return '';
  
  const headers = Object.keys(rows[0]);
  const csvRows = [headers.join(',')];
  
  for (const row of rows) {
    const values = headers.map(header => {
      const val = row[header];
      if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val ?? '';
    });
    csvRows.push(values.join(','));
  }
  
  return csvRows.join('\n');
}

/**
 * Download blob as file
 * @param {Blob} blob
 * @param {string} filename
 */
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Record reliability export event
 * @param {string} type
 * @param {Object} context
 */
function recordReliabilityExport(type, context) {
  console.log('[ReliabilityExport]', { type, context, timestamp: Date.now() });
  
  if (typeof window !== 'undefined' && window.__asterpos_metrics) {
    window.__asterpos_metrics.recordReliabilityExport?.({ type, context });
  }
}

export default {
  exportReliabilitySummaryCsv,
  exportReliabilitySummaryJson,
  exportEntityTableCsv,
  exportBlockedPaymentFollowupCsv,
  exportRepeatedIssuesCsv
};
